/**
 * Client-side sponsored-click beacon (API-1366).
 *
 * Fires a best-effort `POST /api/v1/events/sponsored-click` when a viewer
 * clicks a sponsored event card, so the backend can record a `SponsoredClick`
 * (the CTR numerator that feeds advertiser billing-health, API-1364).
 *
 * Transport rationale:
 *   - `keepalive: true` (not `navigator.sendBeacon`) so the request survives
 *     the cross-host `<a>` navigation the sponsored card triggers. `sendBeacon`
 *     cannot set the `x-csrf-token` header, which the backend requires on
 *     state-changing POSTs in prod/staging (`ENABLE_CSRF`) — `keepalive` can.
 *   - `credentials: 'include'` so the auth + `_csrf` cookies ride along; the
 *     backend derives the viewer identity server-side from the verified session
 *     (or the request IP). This client never sends a viewer id — see the route's
 *     TRUST BOUNDARY note.
 *
 * Best-effort: any failure (offline, misconfigured base URL, 4xx) is swallowed.
 * A missed telemetry beacon must never affect the navigation the user clicked.
 */

/** Surfaces a sponsored card can be clicked from (mirrors the backend enum). */
export type SponsoredClickSurface = 'discover' | 'category' | 'happening-now' | 'map' | 'home-feed';

/** M1 click targets (map-pin is M2 and rejected by the backend). */
export type SponsoredClickTarget = 'card' | 'ticket-cta';

export interface SponsoredClickBeaconInput {
  listingId: string;
  surface: SponsoredClickSurface;
  clickTarget: SponsoredClickTarget;
}

const SPONSORED_CLICK_PATH = '/api/v1/events/sponsored-click';

/** Backend origin, mirroring the RTK client's `NEXT_PUBLIC_API_URL` resolution. */
function resolveApiBase(): string | null {
  const raw = process.env.NEXT_PUBLIC_API_URL;
  if (!raw) return null;
  return raw.replace(/\/+$/, '');
}

/** Read the double-submit `_csrf` cookie the backend issued (JS-readable). */
function readCsrfToken(): string | undefined {
  // `document.cookie` is absent in some sandboxed iframes and jsdom-less setups
  // even when `document` itself is defined.
  if (typeof document === 'undefined' || typeof document.cookie !== 'string') return undefined;
  const match = document.cookie.split('; ').find((cookie) => cookie.startsWith('_csrf='));
  if (!match) return undefined;
  const idx = match.indexOf('=');
  return idx >= 0 ? match.slice(idx + 1) : undefined;
}

/** Endpoint that mints the double-submit `_csrf` cookie. */
const CSRF_TOKEN_PATH = '/api/v1/auth/csrf-token';

/** Warm at most once per page — the cookie has a 24h TTL. */
let csrfWarmRequested = false;

/**
 * Ensure the `_csrf` cookie exists BEFORE a click needs it.
 *
 * The click POST is CSRF-protected in prod/staging (`ENABLE_CSRF`), and the
 * double-submit header can only be set from a cookie the browser already holds.
 * On a public surface (anonymous Discover) nothing warms it: the events fetch is
 * server-side, so the browser never receives the backend's `Set-Cookie`. The
 * beacon would then omit `x-csrf-token`, lusca would 403, and every anonymous
 * sponsored click would be silently dropped — zeroing the CTR numerator for
 * exactly the visitors the metric is about.
 *
 * Cheap and idempotent: no-ops when the cookie is already present, and fires at
 * most one GET per page. Fire-and-forget; a failure just leaves the click
 * unrecorded, never breaking navigation.
 */
export function warmSponsoredClickCsrf(): void {
  if (typeof fetch === 'undefined') return;
  if (csrfWarmRequested) return;
  if (readCsrfToken()) return;

  const base = resolveApiBase();
  if (base === null) return;

  csrfWarmRequested = true;
  try {
    void fetch(`${base}${CSRF_TOKEN_PATH}`, { method: 'GET', credentials: 'include' }).catch(
      () => {},
    );
  } catch {
    // `fetch` can throw synchronously (e.g. a malformed base URL).
  }
}

/**
 * Send the sponsored-click beacon. Fire-and-forget — returns immediately and
 * never throws. No-ops when there's no `fetch`, no configured API base, or a
 * blank `listingId`.
 */
export function beaconSponsoredClick(input: SponsoredClickBeaconInput): void {
  // The whole body is guarded: `fetch` can throw synchronously (malformed URL),
  // and `JSON.stringify` can throw on an exotic input. The docstring promises
  // this never affects navigation, so nothing may escape.
  try {
    if (typeof fetch === 'undefined') return;
    if (!input.listingId) return;

    const base = resolveApiBase();
    if (base === null) return;

    const csrfToken = readCsrfToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (csrfToken) headers['x-csrf-token'] = csrfToken;

    void fetch(`${base}${SPONSORED_CLICK_PATH}`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers,
      body: JSON.stringify({
        listingId: input.listingId,
        surface: input.surface,
        clickTarget: input.clickTarget,
      }),
    }).catch(() => {
      // Best-effort telemetry — a failed beacon must never affect navigation.
    });
  } catch {
    // Same contract: swallow, never throw into the click handler.
  }
}

/** Test-only: reset the once-per-page warm latch. */
export function __resetCsrfWarmForTests(): void {
  csrfWarmRequested = false;
}
